/**
 * EmailReplyService - Manages client email replies
 *
 * SPEC-007: Email Reply Management
 * Provides CRUD operations for EmailReply model
 *
 * Following CLAUDE.md Rule 20: Service Singleton Pattern
 */

import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { ServiceError, ErrorType } from '../utils/StandardErrorFormat.js';
import emailService from './EmailService.js';

class EmailReplyService {
  constructor() {
    this.client = generateClient();
    this.logger = logger.child('EmailReplyService');
    this.validStatuses = ['UNREAD', 'READ', 'REPLIED', 'ARCHIVED'];
    // P2-B: AC5 - Rate limiting: track send timestamps in memory
    this._sendTimestamps = [];
  }

  /**
   * P2-B: AC5 - Check rate limit before sending a reply
   * Allows max 10 sends in any rolling 60-second window.
   *
   * @private
   * @throws {ServiceError} If rate limit is exceeded
   */
  _checkRateLimit() {
    const now = Date.now();
    const windowMs = 60_000;
    const maxPerWindow = 10;

    // Prune timestamps older than 60 seconds
    this._sendTimestamps = this._sendTimestamps.filter(ts => now - ts < windowMs);

    if (this._sendTimestamps.length >= maxPerWindow) {
      throw new ServiceError(
        'Rate limit exceeded. Maximum 10 replies per minute.',
        ErrorType.VALIDATION_ERROR,
        { sendCount: this._sendTimestamps.length, windowMs }
      );
    }
  }

  /**
   * AC-007-09: Capture incoming client reply
   *
   * @param {Object} replyData - Reply data
   * @param {string} replyData.userId - Advisor user ID
   * @param {string} replyData.emailMessageId - Original email message ID
   * @param {string} replyData.campaignId - Campaign ID
   * @param {string} replyData.clientId - Client ID
   * @param {string} replyData.clientEmail - Client email address
   * @param {string} replyData.subject - Reply subject
   * @param {Object} replyData.replyContent - Reply content { text, html }
   * @param {string} replyData.receivedAt - ISO timestamp
   * @param {string} [replyData.sentiment] - Optional sentiment (POSITIVE, NEUTRAL, NEGATIVE)
   * @returns {Promise<Object>} Created reply record
   * @throws {ServiceError} If required fields missing or creation fails
   */
  async captureReply(replyData) {
    this.logger.info('Capturing client reply', {
      emailMessageId: replyData.emailMessageId,
      campaignId: replyData.campaignId,
      clientId: replyData.clientId
    });

    // Validate required fields
    const requiredFields = [
      'userId',
      'emailMessageId',
      'campaignId',
      'clientId',
      'clientEmail',
      'subject',
      'replyContent',
      'receivedAt'
    ];

    const missingFields = requiredFields.filter(field => !replyData[field]);
    if (missingFields.length > 0) {
      this.logger.error('Missing required fields', { missingFields });
      throw new ServiceError(
        `Missing required fields: ${missingFields.join(', ')}`,
        ErrorType.VALIDATION_ERROR,
        { missingFields }
      );
    }

    try {
      // Serialize replyContent as JSON string
      const replyContentStr = typeof replyData.replyContent === 'string'
        ? replyData.replyContent
        : JSON.stringify(replyData.replyContent);

      // Create EmailReply record
      const { data, errors } = await this.client.models.EmailReply.create({
        userId: replyData.userId,
        emailMessageId: replyData.emailMessageId,
        campaignId: replyData.campaignId,
        clientId: replyData.clientId,
        clientEmail: replyData.clientEmail,
        subject: replyData.subject,
        replyContent: replyContentStr,
        receivedAt: replyData.receivedAt,
        status: 'UNREAD', // Always default to UNREAD
        sentiment: replyData.sentiment || undefined
      });

      if (errors) {
        this.logger.error('GraphQL errors creating reply', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to create email reply',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Reply captured successfully', { replyId: data.id });

      // Parse replyContent back to object for response
      return {
        ...data,
        replyContent: JSON.parse(data.replyContent)
      };
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error capturing reply', { error: error.message });
      throw new ServiceError(
        `Failed to capture reply: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-10: Get reply by ID
   *
   * @param {string} replyId - Reply ID
   * @returns {Promise<Object>} Reply record with parsed replyContent
   * @throws {ServiceError} If reply not found or fetch fails
   */
  async getReplyById(replyId) {
    this.logger.info('Fetching reply by ID', { replyId });

    try {
      const { data, errors } = await this.client.models.EmailReply.get({
        id: replyId
      });

      if (errors || !data) {
        this.logger.error('Reply not found', { replyId, errors });
        throw new ServiceError(
          errors?.[0]?.message || `Reply not found: ${replyId}`,
          ErrorType.NOT_FOUND,
          { replyId, errors }
        );
      }

      this.logger.debug('Reply fetched successfully', { replyId });

      // Parse JSON fields
      return this._parseReplyRecord(data);
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error fetching reply', { replyId, error: error.message });
      throw new ServiceError(
        `Failed to fetch reply: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-11: List replies by user
   *
   * @param {string} userId - Advisor user ID
   * @param {Object} [options] - Filter options
   * @param {string} [options.status] - Filter by status
   * @returns {Promise<Array>} Array of reply records
   * @throws {ServiceError} If fetch fails
   */
  async listRepliesByUser(userId, options = {}) {
    this.logger.info('Listing replies by user', { userId, options });

    try {
      const filter = { userId: { eq: userId } };

      // Add optional status filter
      if (options.status) {
        filter.status = { eq: options.status };
      }

      const { data, errors } = await this.client.models.EmailReply.list({
        filter
      });

      if (errors) {
        this.logger.error('GraphQL errors listing replies', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to list email replies',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Replies listed successfully', { count: data.length });

      // Parse JSON fields for all records
      return data.map(reply => this._parseReplyRecord(reply));
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error listing replies', { userId, error: error.message });
      throw new ServiceError(
        `Failed to list replies: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-12: List replies by email message
   *
   * @param {string} emailMessageId - Email message ID
   * @returns {Promise<Array>} Array of reply records
   * @throws {ServiceError} If fetch fails
   */
  async listRepliesByMessage(emailMessageId) {
    this.logger.info('Listing replies by message', { emailMessageId });

    try {
      const { data, errors } = await this.client.models.EmailReply.list({
        filter: { emailMessageId: { eq: emailMessageId } }
      });

      if (errors) {
        this.logger.error('GraphQL errors listing replies', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to list email replies',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Replies listed successfully', { count: data.length });

      return data.map(reply => this._parseReplyRecord(reply));
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error listing replies', { emailMessageId, error: error.message });
      throw new ServiceError(
        `Failed to list replies: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-13: List replies by campaign
   *
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} Array of reply records
   * @throws {ServiceError} If fetch fails
   */
  async listRepliesByCampaign(campaignId) {
    this.logger.info('Listing replies by campaign', { campaignId });

    try {
      const { data, errors } = await this.client.models.EmailReply.list({
        filter: { campaignId: { eq: campaignId } }
      });

      if (errors) {
        this.logger.error('GraphQL errors listing replies', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to list email replies',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Replies listed successfully', { count: data.length });

      return data.map(reply => this._parseReplyRecord(reply));
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error listing replies', { campaignId, error: error.message });
      throw new ServiceError(
        `Failed to list replies: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-14: Update reply status
   *
   * @param {string} replyId - Reply ID
   * @param {string} newStatus - New status (UNREAD, READ, REPLIED, ARCHIVED)
   * @returns {Promise<Object>} Updated reply record
   * @throws {ServiceError} If status invalid or update fails
   */
  async updateReplyStatus(replyId, newStatus) {
    this.logger.info('Updating reply status', { replyId, newStatus });

    // Validate status
    if (!this.validStatuses.includes(newStatus)) {
      this.logger.error('Invalid status', { newStatus, validStatuses: this.validStatuses });
      throw new ServiceError(
        `Invalid status: ${newStatus}. Must be one of: ${this.validStatuses.join(', ')}`,
        ErrorType.VALIDATION_ERROR,
        { newStatus, validStatuses: this.validStatuses }
      );
    }

    try {
      const { data, errors } = await this.client.models.EmailReply.update({
        id: replyId,
        status: newStatus
      });

      if (errors) {
        this.logger.error('GraphQL errors updating status', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to update reply status',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Reply status updated successfully', { replyId, newStatus });

      return this._parseReplyRecord(data);
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error updating status', { replyId, error: error.message });
      throw new ServiceError(
        `Failed to update status: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * AC-007-15: Mark reply as read (helper method)
   *
   * @param {string} replyId - Reply ID
   * @returns {Promise<Object>} Updated reply record
   * @throws {ServiceError} If update fails
   */
  async markAsRead(replyId) {
    this.logger.info('Marking reply as read', { replyId });
    return this.updateReplyStatus(replyId, 'READ');
  }

  /**
   * AC-007-16: Save advisor response
   *
   * @param {string} replyId - Reply ID
   * @param {Object} responseData - Response data
   * @param {string} responseData.content - Response content
   * @param {string} responseData.method - Response method (MANUAL or AI)
   * @returns {Promise<Object>} Updated reply record
   * @throws {ServiceError} If save fails
   */
  async saveAdvisorResponse(replyId, responseData) {
    this.logger.info('Saving advisor response', { replyId, method: responseData.method });

    try {
      // Create advisorReply object with sentAt timestamp
      const advisorReply = {
        sentAt: new Date().toISOString(),
        content: responseData.content,
        method: responseData.method
      };

      const { data, errors } = await this.client.models.EmailReply.update({
        id: replyId,
        status: 'REPLIED', // Auto-update status
        advisorReply: JSON.stringify(advisorReply)
      });

      if (errors) {
        this.logger.error('GraphQL errors saving response', { errors });
        throw new ServiceError(
          errors[0]?.message || 'Failed to save advisor response',
          ErrorType.NETWORK_ERROR,
          { errors }
        );
      }

      this.logger.info('Advisor response saved successfully', { replyId });

      return this._parseReplyRecord(data);
    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error saving response', { replyId, error: error.message });
      throw new ServiceError(
        `Failed to save advisor response: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * REPLY-013: Send outbound reply via AWS SES
   *
   * Workflow:
   *  1. Validate inputs
   *  2. Fetch the originating EmailReply to get threading context
   *  3. Create an OUTBOUND EmailMessage record (status: QUEUED)
   *  4. Call EmailService.sendViaSES() to dispatch via Lambda / SES
   *  5. Update EmailMessage status (SENT or FAILED) with SES message ID
   *  6. Update EmailReply status to REPLIED and persist sentAt timestamp
   *  7. Record a SENT EmailEvent
   *  8. Return { success, emailMessageId, sesMessageId }
   *
   * @param {string} replyId - The INBOUND EmailReply ID being responded to
   * @param {Object} sendParams - Send parameters
   * @param {string} sendParams.to - Recipient email address
   * @param {string} sendParams.subject - Email subject
   * @param {string} sendParams.htmlContent - HTML body of the reply
   * @param {string} [sendParams.textContent] - Plain-text body (auto-stripped if absent)
   * @param {string} [sendParams.replyTo] - Optional Reply-To address
   * @returns {Promise<{success: boolean, emailMessageId: string, sesMessageId: string}>}
   * @throws {ServiceError} If validation fails or SES call throws
   */
  async sendReplyEmail(replyId, { to, subject, htmlContent, textContent, replyTo } = {}) {
    this.logger.info('Sending reply email via SES', { replyId, to, subject });

    // 1. Validate inputs
    if (!replyId) {
      throw new ServiceError(
        'replyId is required',
        ErrorType.VALIDATION_ERROR,
        { missingFields: ['replyId'] }
      );
    }
    if (!to) {
      throw new ServiceError(
        'to address is required',
        ErrorType.VALIDATION_ERROR,
        { missingFields: ['to'] }
      );
    }
    if (!htmlContent) {
      throw new ServiceError(
        'htmlContent is required',
        ErrorType.VALIDATION_ERROR,
        { missingFields: ['htmlContent'] }
      );
    }

    // P2-B: AC5 - Enforce rate limit before any work
    this._checkRateLimit();

    try {
      // 2. Fetch original reply for threading context
      const originalReply = await this.getReplyById(replyId);

      // P2-A: AC3 - Fetch the original EmailMessage to get its SES message ID
      // so we can pass In-Reply-To / References headers for email threading
      let originalSesMessageId = null;
      if (originalReply.emailMessageId) {
        const { data: originalMsg } = await this.client.models.EmailMessage.get({
          id: originalReply.emailMessageId
        });
        originalSesMessageId = originalMsg?.sesMessageId || null;
      }

      // 3. Create OUTBOUND EmailMessage record
      const fromAddress = await emailService.getSenderEmail(originalReply.userId);
      const plainText = textContent || emailService.stripHtml(htmlContent);

      const { data: emailMessage, errors: createErrors } = await this.client.models.EmailMessage.create({
        userId: originalReply.userId,
        clientId: originalReply.clientId,
        campaignId: originalReply.campaignId,
        direction: 'OUTBOUND',
        inReplyTo: originalReply.emailMessageId,
        subject,
        from: fromAddress,
        to: [to],
        bodyHtml: htmlContent,
        bodyText: plainText,
        status: 'QUEUED'
      });

      if (createErrors) {
        this.logger.error('GraphQL errors creating outbound EmailMessage', { createErrors });
        throw new ServiceError(
          createErrors[0]?.message || 'Failed to create outbound EmailMessage',
          ErrorType.NETWORK_ERROR,
          { createErrors }
        );
      }

      const emailMessageId = emailMessage.id;

      // P2-A: AC3 - Build threading headers when the original SES message ID is available
      const threadingHeaders = originalSesMessageId
        ? { inReplyToHeader: originalSesMessageId, referencesHeader: originalSesMessageId }
        : {};

      // 4. Send via SES through EmailService
      const sendResult = await emailService.sendViaSES({
        messageId: emailMessageId,
        to,
        subject,
        bodyHtml: htmlContent,
        bodyText: plainText,
        from: fromAddress,
        ...(replyTo ? { replyTo } : {}),
        ...threadingHeaders,
        // #471: a personal reply carries no open pixel (the sender injects
        // one for callers that do not, when TRACKING_DOMAIN is set)
        enableTracking: false
      });

      // P2-B: AC5 - Record successful send timestamp for rate limiting
      this._sendTimestamps.push(Date.now());

      // 5. Update EmailMessage with send status
      await this.client.models.EmailMessage.update({
        id: emailMessageId,
        status: sendResult.success ? 'SENT' : 'FAILED',
        sentAt: sendResult.success ? new Date().toISOString() : null,
        sesMessageId: sendResult.sesMessageId || null,
        errorMessage: sendResult.error || null
      });

      // P2-C: AC6 - Include deliveryStatus in the advisorReply JSON blob
      // 6. Update EmailReply to REPLIED with sentAt timestamp and delivery status
      const advisorReply = {
        sentAt: new Date().toISOString(),
        content: htmlContent,
        method: 'MANUAL',
        deliveryStatus: 'SENT'
      };

      await this.client.models.EmailReply.update({
        id: replyId,
        status: 'REPLIED',
        advisorReply: JSON.stringify(advisorReply)
      });

      // 7. Record SENT event
      if (sendResult.success) {
        await this.client.models.EmailEvent.create({
          userId: originalReply.userId,
          emailMessageId,
          clientId: originalReply.clientId,
          campaignId: originalReply.campaignId,
          eventType: 'SENT',
          timestamp: new Date().toISOString()
        });
      }

      this.logger.info('Reply email sent successfully', {
        replyId,
        emailMessageId,
        sesMessageId: sendResult.sesMessageId
      });

      // 8. Return result
      return {
        success: sendResult.success,
        emailMessageId,
        sesMessageId: sendResult.sesMessageId || null,
        error: sendResult.error || null
      };

    } catch (error) {
      if (error instanceof ServiceError) throw error;

      this.logger.error('Error sending reply email', { replyId, error: error.message });
      throw new ServiceError(
        `Failed to send reply email: ${error.message}`,
        ErrorType.NETWORK_ERROR,
        { originalError: error.message }
      );
    }
  }

  /**
   * Helper: Parse JSON fields in reply record
   *
   * @private
   * @param {Object} reply - Raw reply record
   * @returns {Object} Parsed reply record
   */
  _parseReplyRecord(reply) {
    const parsed = { ...reply };

    // Parse replyContent if it's a string
    if (reply.replyContent && typeof reply.replyContent === 'string') {
      try {
        // First parse
        let parsedOnce = JSON.parse(reply.replyContent);

        // Check if it's still a string (double-encoded JSON from old Lambda version)
        // Keep this for backward compatibility with existing emails
        if (typeof parsedOnce === 'string') {
          parsedOnce = JSON.parse(parsedOnce);
        }

        parsed.replyContent = parsedOnce;
      } catch (error) {
        this.logger.warn('Failed to parse replyContent', {
          replyId: reply.id,
          error: error.message
        });
        // Keep as string if parsing fails
      }
    }

    // Parse advisorReply if it's a string
    if (reply.advisorReply && typeof reply.advisorReply === 'string') {
      try {
        parsed.advisorReply = JSON.parse(reply.advisorReply);
      } catch (error) {
        this.logger.warn('Failed to parse advisorReply', {
          replyId: reply.id,
          error: error.message
        });
        // Keep as string if parsing fails
      }
    }

    return parsed;
  }
}

// Create singleton instance
const emailReplyServiceInstance = new EmailReplyService();

// Export singleton by default (CLAUDE.md Rule 20)
export default emailReplyServiceInstance;

// Also export class for testing
export { EmailReplyService };
